/**
 * REPORTS — the generated output, and the one thing on this screen that prints.
 *
 * Three sections:
 *   · the four books' bespoke charts, on the current filter;
 *   · the single-day report view, which is all of `/dashboard/view-edit-export`;
 *   · "Send daily report", which emails today's report and has nothing to do
 *     with the date control beside it — which is exactly why it never belonged
 *     in the filter strip it used to live in.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createItem } from '../../../../requests/requests';
import toast from '../../../../../utils/toast';
import { AppButton } from '../../../../button/app-button';
import { PanelShell, ChartShellState } from '../charts/chart-shell';
import {
  DataTable,
  PanelToggle,
  TableScroller,
  Td,
  Th,
} from '../charts/chart.styled';
import { PanelGrid } from '../dashboard-screen.styled';
import { SectionCaption } from '../panels/panels.styled';
import { BookCharts } from './book-charts';
import { DailyReportConfirm } from './daily-report-confirm';
import { DashboardFilter, FilterMeta } from '../state/filter-model';
import { FilterAction } from '../state/filter-reducer';
import { DashboardData, RECORDS_PAGE_SIZE, scopeQuery } from '../data/dashboard-data';
import { BOOK_GROUP, PANEL, reportsPanels } from '../data/panel-plan';
import {
  AnalyticsBatchOk,
  fetchAnalyticsBatch,
  isBatchFailure,
} from '../../../../../api/analytics-client';
import {
  CATEGORICAL_SLOTS,
  PieChartData,
  foldToCap,
} from '../charts/chart-palette';
import { format } from '../charts/chart-theme';
import { stripHtmlTags } from '../../../../../function-utils/string-converter';
import { formatDayKey, zoneLabel } from '../../../../../function-utils/building-time';
import { useClock } from '../../../../../context/clock-context';
import { ClockNote } from '../../../../clock-note/clock-note';

/* eslint-disable @typescript-eslint/no-explicit-any */
type LogRow = Record<string, any>;
/* eslint-enable @typescript-eslint/no-explicit-any */

/** The columns a day's log actually has, minus the four nothing renders and
 *  minus any column that is empty on every row.
 *
 *  A NON-STRING VALUE IS DROPPED rather than rendered: a non-null, non-array
 *  object as a React child throws, and with no error boundary anywhere in this
 *  app the throw unmounts the whole root — the blank "Read the log" screen. The
 *  server's allow-list in serializeLog is the real fix; this is the belt. */
const dayColumns = (rows: LogRow[]): string[] => {
  if (!rows.length) return [];
  const hidden = new Set(['userId', 'id', 'updatedAt', 'property_ids', 'utcCreatedAt']);
  const isObject = (value: unknown) =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

  return Object.keys(rows[0]).filter(
    key =>
      !hidden.has(key) &&
      rows.some(row => row[key]) &&
      !rows.some(row => isObject(row[key]))
  );
};

const dayCell = (key: string, value: unknown, tz: string): string => {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (value == null) return '';
  if (typeof value === 'object') return '';
  // An instant, on the clock the note above the table names.
  if (key === 'createdAt') return format.instant(String(value), tz);

  // stripHtmlTags returns `undefined` for a non-string, which is precisely the
  // case this guard exists for — a non-string reaching a <td> is what unmounts
  // the root, and an empty cell is the honest rendering of it.
  return stripHtmlTags(String(value)) ?? '';
};

export interface ReportsTabProps {
  filter: DashboardFilter;
  filterMeta?: FilterMeta;
  dispatch: (action: FilterAction, opts?: { replace?: boolean }) => void;
  data: DashboardData;
  isMobile: boolean;
  onExportPanelPng: (panelId: string, svg: SVGSVGElement | null) => void;
}

export const ReportsTab: React.FC<ReportsTabProps> = ({
  filter,
  filterMeta,
  dispatch,
  data,
  isMobile,
  onExportPanelPng,
}) => {
  /* THE CLOCK THE SINGLE-DAY VIEW IS SHOWN ON — the /logs answer's own root
     `tz` (R-T12): the reader's for an owner or a manager. The desk day ITSELF
     is always the building's (`window.tz`), whoever asks: an owner sees the
     same rows the concierge had, with their clock times converted. */
  const dayClock = useClock({ buildingId: filter.buildingId, response: data.logs.data });
  const tz = dayClock.zone;
  const deskDay = data.logs.data?.window || null;

  /* ── THE PERIOD-WIDE ANSWER FOR THE TWO BOOK CHARTS (QA-03) ───────────────
   *
   * These two panels used to aggregate `data.records.data.rows` — one page of
   * fifty at the current offset — under titles that name the whole period. This
   * asks the analytics engine the same two questions over the whole
   * server-side filtered result.
   *
   * ITS OWN REQUEST, not a slot in the screen's shared batch, and the reason is
   * in panel-plan.ts beside REPORTS_PANELS: the batch carries ONE `groups`
   * filter for every panel, and these two must be narrowed to the selected BOOK.
   * Putting them in the shared plan would either narrow the KPI strip to the
   * book as well — silently changing what the headline figures count — or leave
   * these two counting all four books under a title that names one.
   *
   * FAILURE IS NOT FATAL. `/analytics/*` is permission-gated and a caller who
   * can read Records may not reach it. On a failure the state stays null and
   * BookCharts falls back to the page-scoped aggregation WITH its caveat, which
   * is the honest degraded answer rather than an empty panel.
   */
  const [serverSeries, setServerSeries] = useState<{
    daily: { day: string; all: number }[];
    kinds: PieChartData[];
    total: number | null;
  } | null>(null);

  const group = BOOK_GROUP[filter.book];
  // Serialised, so the effect depends on the VALUES of the scope rather than on
  // the identity of a fresh object built every render.
  const scopeKey = JSON.stringify({ ...scopeQuery(filter), group, book: filter.book });

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    (async () => {
      try {
        const answer = await fetchAnalyticsBatch(
          reportsPanels(filter.book),
          // `groups` is overridden rather than merged: the reader's own activity
          // group chips describe the SCREEN, and a chart titled "Types of
          // visitors" that silently also counted parcels because a chip was set
          // would be the same class of untruth this fix exists to remove.
          { ...scopeQuery(filter), groups: group ? [group] : [] },
          { signal: controller.signal }
        );
        if (!live) return;

        const slot = (id: string): AnalyticsBatchOk | null => {
          const found = (answer.results || []).find(r => r.id === id);

          return found && !isBatchFailure(found) ? (found as AnalyticsBatchOk) : null;
        };

        const daySlot = slot(PANEL.reportsDaily);
        const kindSlot = slot(PANEL.reportsKind);
        if (!daySlot) {
          setServerSeries(null);

          return;
        }

        const daily = (daySlot.items || []).map(item => ({
          day: item.key,
          all: typeof item.value === 'number' ? item.value : Number(item.value) || 0,
        }));

        // The same fold the page-scoped pie used, so switching source does not
        // also change how many slices are named.
        const kinds = kindSlot
          ? foldToCap(
              (kindSlot.items || []).map(item => ({
                name: item.label || item.key,
                value:
                  typeof item.value === 'number'
                    ? item.value
                    : Number(item.value) || 0,
              })),
              CATEGORICAL_SLOTS
            )
          : [];

        setServerSeries({
          daily,
          kinds,
          total:
            typeof daySlot.total === 'number'
              ? daySlot.total
              : Number(daySlot.total) || null,
        });
      } catch {
        if (live) setServerSeries(null);
      }
    })();

    return () => {
      live = false;
      controller.abort();
    };
    // eslint-disable-next-line
  }, [scopeKey]);

  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [visitorView, setVisitorView] = useState(false);
  const sendRef = useRef(false);

  const buildingName =
    filter.buildingId != null
      ? filterMeta?.buildings.find(b => b.id === filter.buildingId)?.name
      : undefined;

  /**
   * Emails today's report for the selected building.
   *
   * BEHIND A CONFIRMATION SINCE WP-41 / R-23. This used to be the `onClick` of
   * the button itself, so pressing it emailed everyone senior to the sender who
   * covers the building — plus every owner in the organisation — with nothing on
   * screen naming a single one of them. It is now only reachable from
   * <DailyReportConfirm>, which resolves that audience server-side and shows it
   * first.
   *
   * No zone travels with either request: the report is the BUILDING's desk day
   * on the building's clock whoever sends it, so the preview and the send
   * resolve the same day by construction.
   */

  const sendDailyReport = useCallback(async () => {
    if (sendRef.current) return;
    if (filter.buildingId == null) {
      toast.error('No building selected.');

      return;
    }
    try {
      sendRef.current = true;
      setSending(true);
      await createItem('daily-report', {
        property_ids: filter.buildingId,
      });
      toast.success('Report sent!');
      setConfirming(false);
    } catch (error) {
      toast.error('Error sending report.');
    } finally {
      sendRef.current = false;
      setSending(false);
    }
  }, [filter.buildingId]);

  const chartState: ChartShellState = {
    loading: data.records.loading,
    stale: data.records.stale,
    error: data.records.error,
    onRetry: data.records.retry,
  };

  /* WHAT THE CHARTS BELOW ARE ACTUALLY COUNTING.
   *
   * They aggregate `data.records.data.rows`, which is one page of
   * RECORDS_PAGE_SIZE at offset `page * RECORDS_PAGE_SIZE` — not the period.
   * Silent on page 1 of 1, because a caveat that is always on is a caveat nobody
   * reads and here it would also be false: fewer rows than a full page IS the
   * whole result. It appears the moment the result is bigger than what is drawn.
   */
  const bookRows = data.records.data?.rows || [];
  const partialPage =
    filter.page > 0 || bookRows.length === RECORDS_PAGE_SIZE;
  const bookChartScope = partialPage
    ? `Drawn from the ${bookRows.length} rows on page ${
        filter.page + 1
      } of the Records table, not from the whole period.`
    : '';

  const logs = data.logs.data;
  const activeLogs = visitorView ? logs?.visitorLogs || [] : logs?.dailyLogs || [];
  const columns = dayColumns(activeLogs);

  return (
    <PanelGrid data-testid="panel-grid">
      <BookCharts
        book={filter.book}
        rows={data.records.data?.rows || []}
        scopeNote={bookChartScope}
        serverDaily={serverSeries ? serverSeries.daily : null}
        serverKinds={serverSeries ? serverSeries.kinds : null}
        serverTotal={serverSeries ? serverSeries.total : null}
        state={chartState}
        isMobile={isMobile}
        seriesOff={filter.seriesOff}
        onToggleSeries={(panelId, key) =>
          dispatch({ type: 'toggleMulti', field: 'seriesOff', value: `${panelId}:${key}` })
        }
        onExportPng={onExportPanelPng}
      />

      {/* ── THE SINGLE-DAY VIEW ──
          All of /dashboard/view-edit-export. It needs a building AND a day, and
          it says which two it is showing rather than leaving the reader to look
          back up at the controls. */}
      <PanelShell
        id="day-report"
        title={visitorView ? 'Visitor logs' : 'Building activities'}
        wide
        state={{
          loading: data.logs.loading,
          stale: data.logs.stale,
          error: data.logs.error,
          onRetry: data.logs.retry,
        }}
        caveat={
          filter.buildingId == null
            ? 'Choose a building above to load a single day.'
            : !filter.from
            ? `${buildingName || 'This building'} — pick a custom date range above and this shows the first day of it.`
            : `${buildingName || 'This building'} — ${formatDayKey(filter.from, { day: 'numeric', month: 'short', year: 'numeric' })}. The desk day runs 07:00 to 07:00 on the building's own clock.`
        }
        emptyReason={
          filter.buildingId == null || !filter.from
            ? {
                title: 'Pick a building and a day',
                text: 'This view is one building on one desk day. Choose both above and the logs for that day appear here.',
              }
            : data.logs.data && !activeLogs.length
            ? {
                title: `No ${visitorView ? 'visitor' : 'daily'} logs for this day`,
                text: `Try another date or building, or switch to the ${
                  visitorView ? 'daily' : 'visitor'
                } logs.`,
              }
            : null
        }
        controls={
          <>
            <PanelToggle
              type="button"
              active={!visitorView}
              aria-pressed={!visitorView}
              data-testid="day-daily"
              onClick={() => setVisitorView(false)}
            >
              Daily
            </PanelToggle>
            <PanelToggle
              type="button"
              active={visitorView}
              aria-pressed={visitorView}
              data-testid="day-visitor"
              onClick={() => setVisitorView(true)}
            >
              Visitor
            </PanelToggle>
            {/* "REVIEW", NOT "SEND" (R-23). The verb now matches what pressing
                it does: it opens the confirmation that names the recipients,
                the building, the date and the format. The send itself is the
                second press, inside that dialog. */}
            <AppButton
              disabled={filter.buildingId == null}
              onClick={() => setConfirming(true)}
            >
              Review daily report
            </AppButton>
          </>
        }
      >
        {activeLogs.length ? (
          <>
            <SectionCaption>
              {activeLogs.length.toLocaleString()} row
              {activeLogs.length === 1 ? '' : 's'}. To correct one, open it from the
              Records tab — an edit there is written to the record and appears in the
              corrections log.
            </SectionCaption>
            <ClockNote
              clock={dayClock}
              suffix={
                deskDay
                  ? `· the building's desk day of ${formatDayKey(deskDay.day, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}, 07:00 to 07:00 ${zoneLabel(deskDay.tz) || deskDay.tz} time`
                  : undefined
              }
            />
            <TableScroller>
              <DataTable data-testid="day-report-table">
                <thead>
                  <tr>
                    {columns.map(column => (
                      <Th key={column} scope="col">
                        {format.humanise(column)}
                      </Th>
                    ))}
                  </tr>
                </thead>
                <tbody data-clock-zone={tz}>
                  {activeLogs.map((row, i) => (
                    <tr key={row.id ?? i}>
                      {columns.map(column => (
                        <Td key={column}>{dayCell(column, row[column], tz)}</Td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </TableScroller>
          </>
        ) : null}
      </PanelShell>

      {/* Mounted only while open, so it resolves the recipient list fresh every
          time. Caching it across opens is how a dialog comes to name somebody
          who was moved off the building an hour ago. */}
      {confirming && filter.buildingId != null && (
        <DailyReportConfirm
          buildingId={filter.buildingId}
          sending={sending}
          onConfirm={sendDailyReport}
          onClose={() => setConfirming(false)}
        />
      )}
    </PanelGrid>
  );
};
